// src/shared/components/print/PrintableExpenseReport.jsx
import React, { forwardRef } from 'react';
import styles from './PrintableReport.module.css';
import { formatNumber } from '../../utils/formatters';

const PrintableExpenseReport = forwardRef(({ reportData, filters, settings }, ref) => {
  if (!reportData || !settings) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('ar-IQ', {
        year: 'numeric', month: 'long', day: 'numeric',
      });
    } catch { return dateStr; }
  };

  const expenseTypeLabel = {
    all:     'الكل',
    booking: 'مصاريف الحجوزات',
    general: 'المصاريف العامة',
  };

  return (
    <div ref={ref} className={styles.printContainer}>

      <header className={styles.printHeader}>
        {settings.banner && (
          <img src={settings.banner} alt="Company Banner" className={styles.bannerImage} />
        )}
      </header>

      <main className={styles.printContent}>
        <h1 className={styles.mainTitle}>تقرير المصاريف</h1>

        <div className={styles.filterInfo}>
          <span><strong>من:</strong> {filters?.start_date ? formatDate(filters.start_date) : 'البداية'}</span>
          <span><strong>إلى:</strong> {filters?.end_date ? formatDate(filters.end_date) : 'النهاية'}</span>
          <span><strong>نوع المصروف:</strong> {expenseTypeLabel[filters?.expense_type] || 'الكل'}</span>
          <span style={{ gridColumn: '1 / -1' }}>
            <strong>تاريخ التقرير:</strong> {new Date().toLocaleDateString('ar-IQ')}
          </span>
        </div>

        {/* ── ملخص ── */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>الملخص المالي</h2>
          <div className={styles.tableContainer}>
            <table className={styles.reportTable}>
              <tbody>
                <tr>
                  <td style={{ fontWeight: '600', background: '#f8fafc' }}>إجمالي المصاريف:</td>
                  <td className={styles.amountCell} style={{ fontWeight: '700', fontSize: '12pt', color: '#ef4444' }}>
                    {formatNumber(reportData.total_expenses, 0)} د.ع
                  </td>
                </tr>
                {reportData.total_booking_expenses !== undefined && (
                  <tr>
                    <td style={{ fontWeight: '600', background: '#f8fafc' }}>مصاريف الحجوزات:</td>
                    <td className={styles.amountCell}>
                      {formatNumber(reportData.total_booking_expenses, 0)} د.ع
                    </td>
                  </tr>
                )}
                {reportData.total_general_expenses !== undefined && (
                  <tr>
                    <td style={{ fontWeight: '600', background: '#f8fafc' }}>المصاريف العامة:</td>
                    <td className={styles.amountCell}>
                      {formatNumber(reportData.total_general_expenses, 0)} د.ع
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* ── تفاصيل المصاريف ── */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>
            تفاصيل المصاريف ({reportData.expenses?.length || 0} سجل)
          </h2>
          {reportData.expenses?.length > 0 ? (
            <div className={styles.tableContainer}>
              <table className={styles.reportTable}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>نوع المصروف</th>
                    <th>حساب المصروف</th>
                    <th>الوصف / البيان</th>
                    <th>الحجز المرتبط</th>
                    <th>تاريخ الصرف</th>
                    <th>المبلغ</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.expenses.map((expense, index) => (
                    <tr key={expense.id}>
                      <td style={{ textAlign: 'center' }}>{index + 1}</td>
                      <td style={{ textAlign: 'center' }}>
                        {expense.expense_type === 'booking' ? 'حجز' : 'عام'}
                      </td>
                      <td>{expense.category_name}</td>
                      <td>{expense.description}</td>
                      <td>
                        {expense.booking_details
                          ? expense.booking_details.client.name
                          : 'لا يوجد'}
                      </td>
                      <td style={{ textAlign: 'center' }}>{formatDate(expense.expense_date)}</td>
                      <td className={styles.amountCell}>
                        {formatNumber(expense.amount, 0)} د.ع
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="6" style={{ fontWeight: '700', textAlign: 'right' }}>
                      الإجمالي
                    </td>
                    <td className={styles.amountCell} style={{ fontWeight: '700' }}>
                      {formatNumber(reportData.total_expenses, 0)} د.ع
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <p style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
              لا توجد مصاريف تطابق الفلاتر المحددة.
            </p>
          )}
        </section>
      </main>

      <footer className={styles.printFooter}>
        <hr className={styles.footerLine} />
        <div className={styles.footerContent}>
          {settings.company_name && <p>{settings.company_name}</p>}
          {settings.phone_number && <p>{settings.phone_number}</p>}
          {settings.address      && <p>{settings.address}</p>}
        </div>
      </footer>

    </div>
  );
});

PrintableExpenseReport.displayName = 'PrintableExpenseReport';
export default PrintableExpenseReport;
